import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import TrustBadges from "@/components/TrustBadges";
import HeroSlider from "@/components/HeroSlider";
import { BrandFilmSection, LookbookSection } from "@/components/HomepageSections";
import { Button } from "@/components/ui/button";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState(null); // null = loading, [] = none

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/hero-banners").then(({ data }) => setBanners(data || [])).catch(() => setBanners([]));
  }, []);

  const hasBanners = Array.isArray(banners) && banners.length > 0;

  return (
    <div data-testid="home-page">
      {/* Hero — dynamic banner slider if configured, static fallback otherwise */}
      {hasBanners ? (
        <HeroSlider />
      ) : (
        <section className="relative noise-overlay" data-testid="hero">
          <div className="grid lg:grid-cols-12 min-h-[85vh]">
            <div className="lg:col-span-7 order-2 lg:order-1 px-6 lg:px-16 py-16 lg:py-24 flex flex-col justify-center">
              <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="label-xs opacity-70">
                Fall / Winter Edit — 2026
              </motion.p>
              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
                className="mt-6 text-5xl sm:text-6xl lg:text-[6rem] leading-[0.95] tracking-tighter font-serif">
                Wear Your<br/>Identity<span className="text-3xl align-top">.</span>
              </motion.h1>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.7 }}
                className="mt-8 max-w-md text-base leading-relaxed opacity-70">
                Heavyweight cotton. Considered cuts. A capsule wardrobe engineered for the moments that matter.
              </motion.p>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-10 flex flex-wrap gap-4">
                <Link to="/shop"><Button data-testid="hero-shop-btn" className="rounded-none px-10 py-6 label-xs">Shop the Collection</Button></Link>
                <Link to="/about"><Button variant="outline" data-testid="hero-about-btn" className="rounded-none px-10 py-6 label-xs border-foreground">Our Story</Button></Link>
              </motion.div>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2 relative min-h-[50vh] lg:min-h-[85vh]">
              <img
                src="https://images.unsplash.com/photo-1622519407650-3df9883f76a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Model wearing THE FABRICS"
                className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent" />
            </div>
          </div>
        </section>
      )}

      {/* Marquee */}
      <div className="border-y py-6 marquee bg-foreground text-background">
        <div className="marquee-track font-display text-3xl md:text-5xl font-bold uppercase tracking-tighter">
          {Array.from({ length: 8 }).map((_, i) => (<span key={i} className="mx-8">Wear Your Identity ✦ New Season ✦</span>))}
        </div>
      </div>

      {/* Trust badges */}
      <TrustBadges />

      {/* Brand Film (admin-managed) */}
      <BrandFilmSection />

      {/* Featured Products */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="label-xs opacity-60">Featured</p>
            <h2 className="mt-3 text-4xl lg:text-5xl font-serif tracking-tight">The New Essentials</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 label-xs link-underline" data-testid="view-all-link">View All <ArrowRight className="h-4 w-4"/></Link>
        </div>
        {products.length === 0 ? (
          <div className="text-center py-24 border border-dashed">
            <p className="label-xs opacity-60">No products yet</p>
            <p className="mt-3 font-serif text-2xl">Your capsule begins soon.</p>
            <p className="mt-2 text-sm opacity-60">Add products from the admin panel to see them here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
            {products.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </section>

      {/* Editorial split */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-8 grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        <img src="https://images.unsplash.com/photo-1615540732322-4ae8deb97091?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
             alt="Editorial" className="w-full aspect-[4/5] object-cover" />
        <div>
          <p className="label-xs opacity-60">Craft & Fabric</p>
          <h2 className="mt-4 text-4xl lg:text-5xl font-serif tracking-tight">Made with intent.<br/>Worn with confidence.</h2>
          <p className="mt-8 opacity-70 leading-relaxed max-w-lg">
            Every piece begins on the loom in India. We source 240–260 GSM cotton, garment-wash for softness,
            and finish with reinforced seams. What you feel is decades of textile craft — refined for a modern silhouette.
          </p>
          <Link to="/about"><Button variant="outline" className="mt-10 rounded-none px-10 py-6 label-xs border-foreground" data-testid="editorial-cta">Discover the Story</Button></Link>
        </div>
      </section>

      {/* Lookbook (admin-managed) */}
      <LookbookSection />
    </div>
  );
}
